import { IS_LOGGED, isSecureHost, TOKEN_BASE_NAME, TOKEN_BASE_REFRESH } from 'utility/constant';
import { NextApiRequest, NextApiResponse } from 'next';
import { hasValue, isNotEmpty, isPersonalUrl, isTruthy, purgeNulls } from 'utility/functions';

import { AuthTokenType } from 'features/auth/types';
import { TokenUtilsType } from 'lib/auth/web';
import { UsersGetProfileResponse } from 'types/swagger';
import { setIsOpenLoginModalValue } from 'features/modal/modalSlice';
import { store } from 'sites/snai/lib/store';
import { utcNow } from 'utility/date';
import { NextRequest } from 'next/server';
import { NextApiRequestCookies } from 'next/dist/server/api-utils';
import { IncomingMessage } from 'http';

export interface UserAgentyType {
  xAgent: string;
  xMobile: boolean;
  xCrawler: boolean;
  xFrontend: string;
}

export const parseUserAgent = (req?: IncomingMessage): UserAgentyType => {
  const xAgent = req?.headers?.['user-agent'] ?? '';

  const _agent = xAgent.toLowerCase();

  let xMobile = false;
  // taken from http://detectmobilebrowsers.com/
  if (
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
      _agent
    )
  ) {
    xMobile = true;
  } else if (
    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
      _agent.substring(0, 4)
    )
  ) {
    xMobile = true;
  }

  let xCrawler = false;
  if (
    /abacho|accona|AddThis|AdsBot|ahoy|AhrefsBot|AISearchBot|alexa|altavista|anthill|appie|applebot|arale|araneo|AraybOt|ariadne|arks|aspseek|ATN_Worldwide|Atomz|baiduspider|baidu|bbot|bingbot|bing|Bjaaland|BlackWidow|BotLink|bot|boxseabot|bspider|calif|CCBot|ChinaClaw|christcrawler|CMC\/0\.01|combine|confuzzledbot|contaxe|CoolBot|cosmos|crawler|crawlpaper|crawl|curl|cusco|cyberspyder|cydralspider|dataprovider|digger|DIIbot|DotBot|downloadexpress|DragonBot|DuckDuckBot|dwcp|EasouSpider|ebiness|ecollector|elfinbot|esculapio|ESI|esther|eStyle|Ezooms|facebookexternalhit|facebook|facebot|fastcrawler|FatBot|FDSE|FELIX IDE|fetch|fido|find|Firefly|fouineur|Freecrawl|froogle|gammaSpider|gazz|gcreep|geona|Getterrobo-Plus|get|girafabot|golem|googlebot|\-google|grabber|GrabNet|griffon|Gromit|gulliver|gulper|hambot|havIndex|hotwired|htdig|HTTrack|ia_archiver|iajabot|IDBot|Informant|InfoSeek|InfoSpiders|INGRID\/0\.1|inktomi|inspectorwww|Internet Cruiser Robot|irobot|Iron33|JBot|jcrawler|Jeeves|jobo|KDD\-Explorer|KIT\-Fireball|ko_yappo_robot|label\-grabber|larbin|legs|libwww-perl|linkedin|Linkidator|linkwalker|Lockon|logo_gif_crawler|Lycos|m2e|majesticsEO|marvin|mattie|mediafox|mediapartners|MerzScope|MindCrawler|MJ12bot|mod_pagespeed|moget|Motor|msnbot|muncher|muninn|MuscatFerret|MwdSearch|NationalDirectory|naverbot|NEC\-MeshExplorer|NetcraftSurveyAgent|NetScoop|NetSeer|newscan\-online|nil|none|Nutch|ObjectsSearch|Occam|openstat.ru\/Bot|packrat|pageboy|ParaSite|patric|pegasus|perlcrawler|phpdig|piltdownman|Pimptrain|pingdom|pinterest|pjspider|PlumtreeWebAccessor|PortalBSpider|psbot|rambler|Raven|RHCS|RixBot|roadrunner|Robbie|robi|RoboCrawl|robofox|Scooter|Scrubby|Search\-AU|searchprocess|search|SemrushBot|Senrigan|seznambot|Shagseeker|sharp\-info\-agent|sift|SimBot|Site Valet|SiteSucker|skymob|SLCrawler\/2\.0|slurp|snooper|solbot|speedy|spider_monkey|SpiderBot\/1\.0|spiderline|spider|suke|tach_bw|TechBOT|TechnoratiSnoop|templeton|teoma|titin|topiclink|twitterbot|twitter|UdmSearch|Ukonline|UnwindFetchor|URL_Spider_SQL|urlck|urlresolver|Valkyrie libwww\-perl|verticrawl|Victoria|void\-bot|Voyager|VWbot_K|wapspider|WebBandit\/1\.0|webcatcher|WebCopier|WebFindBot|WebLeacher|WebMechanic|WebMoose|webquest|webreaper|webspider|webs|WebWalker|WebZip|wget|whowhere|winona|wlm|WOLP|woriobot|WWWC|XGET|xing|yahoo|YandexBot|YandexMobileBot|yandex|yeti|Zeus/i.test(
      _agent
    )
  ) {
    xCrawler = true;
  }

  const xFrontend = `${xMobile ? 60 : 1}`;

  return {
    xAgent,
    xMobile,
    xCrawler,
    xFrontend,
  };
};

export const pushCookies = (res: NextApiResponse, token: AuthTokenType | undefined): void => {
  const { accessToken, ...attributes } = (purgeNulls(token) ?? {}) as AuthTokenType;
  res.setHeader('Set-Cookie', [
    `${TOKEN_BASE_REFRESH}=${JSON.stringify(attributes)}; http-only=false; path=/;${isSecureHost ? ' Secure' : ''}.`,
    `${TOKEN_BASE_NAME}=${accessToken}; http-only=false; path=/;${isSecureHost ? ' Secure' : ''}.`,
    `${IS_LOGGED}=${isNotEmpty(attributes?.refreshToken)}; http-only=false; path=/;${isSecureHost ? ' Secure' : ''}.`,
  ]);
};

export const raiseForbidden = (response: NextApiResponse) => {
  response.status(403);
  response.send({ error: 'RefreshToken missing or invalid' });
};

export type RequertType = NextApiRequest | NextRequest | { cookies: NextApiRequestCookies };

export const doTokenCheck = async (request: RequertType): Promise<TokenUtilsType> => {
  const pong = utcNow().getTime();

  let expireDate = pong - 1000;

  let missingAccessToken = true;
  let missingExpireDate = true;
  let hasRefreshToken = false;
  let hasLoggedFlag = false;

  let isAuthenticated = false;

  try {
    hasLoggedFlag = isTruthy(request.cookies?.[IS_LOGGED]);

    const srcAccess = request.cookies?.[TOKEN_BASE_NAME];
    missingAccessToken = isTruthy(srcAccess?.length) ? false : true;

    const { expireDate: srcExpire, refreshToken: srcRefresh } = JSON.parse(
      request.cookies?.[TOKEN_BASE_REFRESH] ?? '{}'
    ) as AuthTokenType;
    missingExpireDate = hasValue(srcExpire) ? false : true;
    hasRefreshToken = isTruthy(srcRefresh?.length);

    expireDate = srcExpire ?? expireDate;
  } catch (e) {
    console.error(e);
    expireDate = pong - 1000;
  } finally {
    isAuthenticated = hasRefreshToken && hasLoggedFlag;
  }
  const threshold = Math.floor((expireDate - pong) * 0.001);

  // for NextApiRequest referer is headers.referer, for NextRequest referer is
  const referer = Reflect.get((request as any)?.headers ?? {}, 'referer') ?? Reflect.get(request ?? {}, 'url') ?? '';

  let status = 200; // OK
  if (missingAccessToken || missingExpireDate || pong > expireDate) {
    if (isAuthenticated) {
      status = 203; // notify client to refresh session
    } else if (isPersonalUrl(referer)) {
      status = 403; // unable to proceede - unathorized
    } else {
      status = 203; // access get unautheticated token
    }
  }

  console.log(
    `#--- TOKEN:   status: ${status}   auth: ${isAuthenticated}   now is: ${new Date(
      pong
    ).toISOString()}   expires: ${new Date(expireDate).toISOString()}   still: ${threshold}" ---#`
  );

  return Promise.resolve({ status, threshold, isAuthenticated });
};

export const callbackOnAuthenticated = <T>(isAuthenticated: boolean, callback: () => T) => {
  isAuthenticated ? callback() : store.dispatch(setIsOpenLoginModalValue(true));
};

export const editUrlWithSessionParam = (url: string, profile: UsersGetProfileResponse) => {
  if (!url.includes('?')) return url;

  const urlParams = new URLSearchParams(url);
  for (const [key, value] of urlParams.entries()) {
    if (value.includes('__')) {
      const decryptedValue = value.replaceAll('__', '');
      urlParams.set(key, profile[decryptedValue]);
    }
  }
  return decodeURIComponent(urlParams.toString());
};
